import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, AlertCircle, Users, TrendingUp, TrendingDown } from 'lucide-react';
import { Amplify } from 'aws-amplify';
import { get } from 'aws-amplify/api';
import awsconfig from '../aws-exports';
import AuthButton from './AuthButton';

Amplify.configure(awsconfig);

console.log("user pool id ", awsconfig.aws_user_pools_id)

export const Home = () => {
    const [ticker, setTicker] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recentAnalyses, setRecentAnalyses] = useState([]);
    const navigate = useNavigate();

    const location = useLocation();

    useEffect(() => {
        fetchRecentAnalyses();
    }, [location]);

    const fetchRecentAnalyses = async () => {
        try {
            const response = await get({
                apiName: 'dcf',
                path: '/latest',
                options: {
                    queryParams: {
                        recent: 'true'
                    }
                }
            }).response.then(response => {
                return response.body.json();
            });

            setRecentAnalyses(response);
            logRecentAnalyses(response);

        } catch (err) {
            console.error('Error fetching recent analyses:', err);
        }
    };

    const handleAnalyze = async () => {
        if (!ticker) {
            setError('Please enter a stock ticker');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await get({
                apiName: 'dcf',
                path: `/stockdata/${ticker}`
            }).response;

            const reader = response.body.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            const data = JSON.parse(text);

            navigate(`/${ticker.toUpperCase()}`, { state: { data } });
        } catch (err) {
            console.error('Analysis error:', err);
            setError('Failed to analyze stock. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAnalyze();
        }
    };

    const logRecentAnalyses = (analyses) => {
        analyses.forEach((analysis) => {
            const valuation = analysis.analysis?.valuation || {};
            const fairValue = valuation.fairValue || 'N/A';
            console.log(`Ticker: ${analysis.ticker}, Recent Fair Value: ${fairValue}`);
        });
    };

    const timeAgo = (timestamp) => {
        const now = new Date();
        const timeDifference = now - new Date(timestamp);
        const minutes = Math.floor(timeDifference / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return "Just now";
        if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
        if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
        return `${days} day${days > 1 ? "s" : ""} ago`;
    };

    return (
        <div className="min-h-screen">
            <div className="flex flex-col items-center">
                {/* Logo and Title Section */}
                <div className="w-full flex flex-col items-center mb-12 mt-8">
                    <img
                        src="/logo.png"
                        alt="Company Logo"
                        className="w-[140px] h-[140px] md:w-[256px] md:h-[256px]"
                    />
                    <h1 className="text-4xl md:text-5xl font-bold mt-6 text-center text-white">
                        The Value Investor
                    </h1>
                </div>

                <div className="max-w-2xl w-full px-4">
                    {/* Auth Button */}
                    <div className="flex justify-end mb-4">
                        <AuthButton />
                    </div>

                    {/* Search card */}
                    <Card className="mb-8">
                        <CardContent className="pt-6">
                            <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Input
                                        placeholder="Enter stock ticker (e.g., AAPL)"
                                        value={ticker}
                                        onChange={(e) => setTicker(e.target.value.toUpperCase())}
                                        onKeyPress={handleKeyPress}
                                        className="flex-1"
                                    />
                                    <Button
                                        onClick={handleAnalyze}
                                        disabled={loading}
                                        className="w-full sm:w-auto"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Analyzing...
                                            </>
                                        ) : (
                                            'Analyze Stock'
                                        )}
                                    </Button>
                                </div>

                                {error && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Activity feed card */}
                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Recent Activity
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {recentAnalyses.map((analysis) => {
                                    const valuation = analysis.analysis?.valuation || {};
                                    const fairValue = parseFloat(valuation.fairValue) || 0;
                                    const currentPrice = parseFloat(valuation.currentPrice) || 0;

                                    return (
                                        <Link
                                            to={`/analysis/${analysis.id}`}
                                            key={analysis.id}
                                            className="block p-2 hover:bg-gray-50 rounded"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium">{analysis.ticker}</div>
                                                    <div className="text-xs text-gray-500">by {analysis.analysis.createdBy}</div>
                                                    <div className="text-xs text-gray-500">{timeAgo(analysis.created_at)}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className={fairValue > currentPrice ? 'text-green-500' : 'text-red-500'}>
                                                        Fair Value: ${fairValue.toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Current: ${currentPrice.toFixed(2)}
                                                    </div>
                                                    <div className={`text-xs flex items-center justify-end gap-1 ${fairValue > currentPrice ? 'text-green-500' : 'text-red-500'}`}>
                                                        {fairValue > currentPrice ?
                                                            <TrendingDown className="h-4 w-4" /> :
                                                            <TrendingUp className="h-4 w-4" />
                                                        }
                                                        {Math.abs(((fairValue - currentPrice) / currentPrice) * 100).toFixed(1)}%
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Home;